import { useState } from 'react';
import { Trans } from 'react-i18next';
import { groupBy } from 'lodash-es';

import { Link, Stack } from '@hotjar/hotsauce';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { sortFeatureGroups } from '@hotjar/webapp/domains/revenue/plan/utils/sortFeatureGroups';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { Site } from '@hotjar/site';

import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { getProductsByProductType } from '@hotjar/webapp/domains/common/product/utils';

import type {
  SubmitCheckoutParams,
  SubmitCheckoutResult,
} from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import { PlanComparisonColumnsContext } from '@hotjar/webapp/features/Billing/PlanComparisonColumns/utils';

import { isTrafficRecommendationReferrer } from '@hotjar/webapp/domains/revenue/upsell/utils';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { PlanColumnsStyles as S } from './styles';
import { HJ_PRICING_PAGE } from '../PlanComparisonModal/constants';
import { PlanColumn } from './PlanColumn';
import type {
  PlanComparisonFeatureGateDetails,
  PlanComparisonReferrer,
  TrafficRecommendationReferrer,
} from '../PlanComparisonModal/types';
import { PlanFeaturesToggle } from '../Upsell/components/PlanFeaturesToggle/PlanFeaturesToggle';

interface Props {
  site?: Site;
  featureGate: PlanComparisonFeatureGateDetails | null;
  recommendedPlanId: string | null | undefined;
  referrer: PlanComparisonReferrer | TrafficRecommendationReferrer | '';
  isUpgradeRequest: boolean;
  modalName: string;
  billingPeriod: BillingPeriod;
  billingPeriodChanged: boolean;
  onCancel: () => void;
  onSubmit: (params: SubmitCheckoutParams) => Promise<SubmitCheckoutResult>;
  onError: (error: Error) => void;
}

export const PlanComparisonColumns = ({
  site,
  featureGate,
  recommendedPlanId,
  referrer,
  isUpgradeRequest,
  modalName,
  billingPeriod,
  billingPeriodChanged,
  onCancel,
  onSubmit,
  onError,
}: Props): JSX.Element => {
  const [showFeaturesList, setShowFeaturesList] = useState<boolean>(false);
  const { data: productCatalog } = useProductCatalog();
  const { click } = useTrack();

  const toggleFeaturesList = () => {
    click(`${modalName} feature list toggle`, modalName, {});
    setShowFeaturesList(!showFeaturesList);
  };

  const products = getProductsByProductType(featureGate?.sku, productCatalog);
  const groupedProducts = groupBy(products, 'tier');

  return (
    <>
      <Stack direction="row" spacing="x1">
        <PlanComparisonColumnsContext.Provider
          // FIXME: This creates a new object on every render and may create unnecessary rerenders.
          //        Fix this or add a comment to explain why it's desired.
          //        More details: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-constructed-context-values.md
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            site,
            recommendedPlanId,
            referrer,
            featureGate,
            isUpgradeRequest: !!isUpgradeRequest,
            onCancel,
            onSubmit,
            onError,
          }}
        >
          {featureGate?.sku &&
            groupedProducts &&
            sortFeatureGroups(Object.keys(groupedProducts) as FeatureGroup[]).map((tier) => (
              <PlanColumn
                key={tier}
                tier={tier}
                tierProducts={groupedProducts[tier]}
                showFeaturesList={showFeaturesList}
                productType={featureGate.sku}
                isPlanAgnostic={featureGate.isPlanAgnostic}
                modalName={modalName}
                billingPeriod={billingPeriod}
                billingPeriodChanged={billingPeriodChanged}
              />
            ))}
        </PlanComparisonColumnsContext.Provider>
      </Stack>

      {showFeaturesList && (
        <S.FindOutMore isTrafficRecommendationModal={isTrafficRecommendationReferrer(referrer)}>
          <Trans i18nKey="revenue:plan-comparison-columns.find-out-more">
            <Link href={HJ_PRICING_PAGE} kind="inline" isExternal>
              Find out more about the different plans
            </Link>
          </Trans>
        </S.FindOutMore>
      )}

      <PlanFeaturesToggle isOpen={showFeaturesList} onClick={toggleFeaturesList} />
    </>
  );
};
